import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthIdentity, useSignOut } from '../../shared/auth/AuthProvider';
import { getSettingsRepository } from '../../shared/services/settings/repositoryFactory';
import { MyApplicationAccess } from '../../shared/services/settings/types';
import { APPLICATIONS } from '../../shared/config/applications';
import { IconButton, useToast, Skeleton, BottomSheet } from '../../shared/components';
import { LogoutIcon, PaletteIcon } from '../../shared/icons';
import { ThemeSwitcher } from '../../shared/theme/ThemeSwitcher';
import { getLastApplication } from '../../shared/auth/entryRedirect';

const settingsRepo = getSettingsRepository();

/**
 * Selector de Aplicacion: primera pantalla despues del login. LCC es una
 * plataforma que aloja varias aplicaciones (Control Operativo, Gestion
 * Humana, ...) - esta pantalla NO pertenece a ninguna de ellas, por eso no
 * usa AppShell/BottomNavigation ni CurrentUserProvider (esos son por
 * aplicacion, ver AppSection).
 *
 * Todo usuario ve TODAS las tarjetas siempre. La que no tenga permiso
 * aparece deshabilitada; tocarla explica por que, en vez de navegar.
 */
export function ApplicationSelectorPage() {
  const navigate = useNavigate();
  const identity = useAuthIdentity();
  const signOut = useSignOut();
  const toast = useToast();
  const [access, setAccess] = useState<MyApplicationAccess[] | null>(null);
  const [themeSheetOpen, setThemeSheetOpen] = useState(false);
  const lastCode = useMemo(() => getLastApplication(), []);

  useEffect(() => {
    settingsRepo.listMyApplications().then(setAccess);
  }, [identity.id]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Buenos días';
    if (h < 19) return 'Buenas tardes';
    return 'Buenas noches';
  }, []);

  const accessByCode = useMemo(() => {
    const map = new Map<string, MyApplicationAccess>();
    (access ?? []).forEach((a) => map.set(a.applicationCode, a));
    return map;
  }, [access]);

  const handleCardClick = (code: string, name: string) => {
    const app = accessByCode.get(code);
    if (!app || !app.isActive) {
      toast.show(`No tienes acceso a ${name}. Pide a un administrador que te registre en esta aplicación.`, 'error');
      return;
    }
    navigate(APPLICATIONS.find((a) => a.code === code)!.homePath);
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-3xl flex-col bg-surface-subtle">
      <div className="safe-top flex items-start justify-between px-4 pb-2 pt-5">
        <div>
          <p className="text-sm text-ink-400">{greeting},</p>
          <h1 className="text-2xl font-bold text-ink-900">{identity.fullName.split(' ')[0]}</h1>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <IconButton
            icon={<PaletteIcon width={20} height={20} />}
            label="Tema visual"
            onClick={() => setThemeSheetOpen(true)}
            className="h-11 w-11 text-ink-400 hover:bg-ink-100 hover:text-brand-600"
          />
          {signOut && (
            <IconButton
              icon={<LogoutIcon width={20} height={20} />}
              label="Cerrar sesión"
              onClick={signOut}
              className="h-11 w-11 text-ink-400 hover:bg-ink-100 hover:text-critical"
            />
          )}
        </div>
      </div>

      <BottomSheet open={themeSheetOpen} onClose={() => setThemeSheetOpen(false)} title="Tema visual">
        <div className="pb-2 pt-1">
          <ThemeSwitcher />
        </div>
      </BottomSheet>

      <div className="flex flex-1 flex-col justify-center gap-4 p-4">
        <p className="px-1 text-sm font-semibold text-ink-500">Elige una aplicación</p>
        {(() => {
          const last = lastCode ? APPLICATIONS.find((a) => a.code === lastCode) : null;
          const lastAccess = last ? accessByCode.get(last.code) : null;
          if (!last || !lastAccess?.isActive) return null;
          return (
            <button
              type="button"
              onClick={() => handleCardClick(last.code, last.name)}
              className="flex items-center justify-between gap-3 rounded-2xl border border-brand-200 bg-brand-light/60 px-4 py-3 text-left text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-light"
            >
              <span>Continuar en {last.name}</span>
              <span aria-hidden>→</span>
            </button>
          );
        })()}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {access === null
            ? APPLICATIONS.map((a) => <Skeleton key={a.code} className="h-36 rounded-2xl" />)
            : APPLICATIONS.map((a) => {
                const app = accessByCode.get(a.code);
                const enabled = !!app && app.isActive;
                const Icon = a.icon;
                return (
                  <button
                    key={a.code}
                    onClick={() => handleCardClick(a.code, a.name)}
                    className={[
                      'flex flex-col items-start gap-3 rounded-2xl border p-5 text-left transition-colors duration-200',
                      enabled ? 'border-ink-100 bg-surface hover:border-brand-300 hover:bg-brand-light/40' : 'cursor-not-allowed border-ink-100 bg-surface opacity-50',
                    ].join(' ')}
                  >
                    <div className={['flex h-11 w-11 items-center justify-center rounded-xl', enabled ? 'bg-brand-light text-brand-600' : 'bg-ink-100 text-ink-400'].join(' ')}>
                      <Icon width={22} height={22} />
                    </div>
                    <div>
                      <p className="text-base font-semibold text-ink-900">{a.name}</p>
                      <p className="text-sm text-ink-400">{a.description}</p>
                    </div>
                    {!enabled && <span className="text-xs font-medium text-critical">Sin acceso</span>}
                    {enabled && a.code === lastCode && <span className="text-xs font-medium text-brand-600">Última que usaste</span>}
                  </button>
                );
              })}
        </div>
      </div>
    </div>
  );
}
